// Native-only: turn the visible surface into the queue we hand the plugin.
//
// THE POINT
// ---------
// Tapping a row used to push a ONE-ITEM queue to the plugin. Everything after
// it arrived one track at a time through the lock-screen refill, so the
// plugin's index space and the surface's had nothing to do with each other.
// That mismatch is what produced, over and over: a track skipped, "next" from
// row 2 landing on row 7, a row tap toggling a different track, and — the one
// that finally made it obvious — prev refusing to go past the row you tapped,
// because with a one-item queue there IS nothing before it.
//
// Push the whole surface and the plugin's index IS the surface's index:
//   prev  = index - 1        (5 → 4 → 3, whether or not you heard 4)
//   next  = index + 1
//   refill only matters past the end of the list
//
// This is a simplification, not an addition. The refill cursor arithmetic and
// most of the guards around playback exist only to reconcile two queues.
//
// Entries use jinke's audioList field names (name / singer / cover / musicSrc)
// so every existing reader — Redux currentTrack, LikeButton, row matching —
// works on them unchanged.

import { resolveCover } from './cover';
import { entityId } from './entityId';
import { sampleUrlOf } from './resolvePlaybackSrc';

/**
 * Map one listContext item to a queue entry, or null when it has no playable
 * sample (mixes, local-file-only rows, tracks with no Apple match).
 */
export function nativeQueueEntry(track) {
    const musicSrc = sampleUrlOf(track);
    if (!musicSrc) return null;
    return {
        name: track.title || track.name || 'Unknown Track',
        singer: track.artist || track.singer || '',
        cover: resolveCover(track.artwork, track.artworkUrl, track.image, track.cover),
        musicSrc,
        // Mirrors what DJPalAudioPreview stamps on a tapped track, so a like
        // fired from the player on an auto-advanced track still writes a
        // complete, playable row.
        samplePreviewUrl: musicSrc,
        trackId: entityId(track),
        ...(track.pageUrl || track.page_url ? { pageUrl: track.pageUrl || track.page_url } : {}),
        ...(Array.isArray(track.artists) && track.artists.length ? { artists: track.artists } : {}),
    };
}

/**
 * Build the queue for a listContext.
 *
 * Unplayable items are dropped, which shifts positions — so `startIndex` is
 * `currentIndex` already translated into the filtered array. Never index the
 * result with a raw listContext index; that mismatch is its own class of bug
 * (it is what made the refill pool start from the wrong place in the feed).
 *
 * @param {Array}  items         listContext.items (raw track objects)
 * @param {number} currentIndex  listContext.currentIndex (index into `items`)
 * @returns {{entries: Array, startIndex: number}}
 */
export function buildNativeQueue(items, currentIndex = 0) {
    const entries = [];
    let startIndex = 0;
    const want = Math.max(0, currentIndex | 0);
    (items || []).forEach((track, i) => {
        const entry = nativeQueueEntry(track);
        if (!entry) return;
        entries.push(entry);
        // Everything up to and including the tapped row has been consumed, so
        // after the loop this holds the tapped row's position in the FILTERED
        // array (or the nearest survivor when the tapped row itself was
        // dropped).
        if (i <= want) startIndex = entries.length - 1;
    });
    return { entries, startIndex: Math.max(0, Math.min(startIndex, entries.length - 1)) };
}

/**
 * Identity of a built queue, for skipping redundant pushes. Order and
 * membership are all the plugin cares about.
 */
export function queueSignature(entries) {
    return (entries || []).map(t => t.musicSrc || '').join('|');
}

/**
 * Splice "Add to Next Up" extras into a surface-built queue.
 *
 * Each extra lands right after the track the plugin was PLAYING when it was
 * added — not after the last tapped row: `listContext.currentIndex` stays on
 * the row the user tapped while the plugin auto-advances (or the user skips)
 * past it, so anchoring at `desired + 1` buried the added track behind the
 * playhead — the feed just played its own next track and the add looked
 * ignored.
 *
 * The anchor is assigned ONCE, at add time, and persists in `anchors`
 * (extra url → anchor url), which the caller must keep across effect runs.
 * A moving anchor is wrong in both directions: re-anchoring to the current
 * track on every run would drag an already-played extra back in front of
 * the playhead (it would replay after every advance), and merely moving the
 * splice point changes the queue signature and forces a spurious re-push.
 *
 * Adding while an earlier extra plays anchors to that extra (chains keep
 * add order); several adds during one track queue in add order. When the
 * anchor can't be resolved (playing a refill rec past the end of the list,
 * or the anchor left the surface) the extra falls back to after the tapped
 * row — the old behavior.
 *
 * @param {Array}  entries     surface queue (buildNativeQueue output, non-empty)
 * @param {Array}  extras      audioList entries not present in `entries`, add order
 * @param {string|null} playingUrl url the plugin currently has loaded
 * @param {number} desired     start position the surface asked for
 * @param {Object} anchors     extra url → anchor url from previous runs
 * @returns {{entries: Array, desired: number, anchors: Object}}
 */
export function spliceNextUpExtras(entries, extras, playingUrl, desired, anchors = {}) {
    if (!extras || !extras.length) return { entries: entries || [], desired, anchors: {} };
    const base = entries || [];
    const desiredUrl = base[Math.max(0, Math.min(desired, base.length - 1))]?.musicSrc || null;
    const extraUrls = new Set(extras.map(t => t.musicSrc));
    const baseUrls = new Set(base.map(t => t.musicSrc));
    const next = {};
    for (const t of extras) {
        const kept = anchors[t.musicSrc];
        if (kept) { next[t.musicSrc] = kept; continue; }
        // New extra: anchor to what's playing — a surface track, or an
        // earlier extra (which by add order is already anchored itself).
        const playingKnown = playingUrl
            && (baseUrls.has(playingUrl) || (extraUrls.has(playingUrl) && playingUrl !== t.musicSrc));
        next[t.musicSrc] = playingKnown ? playingUrl : desiredUrl;
    }
    const result = [...base];
    const placedAfter = {}; // anchor url → how many extras already follow it
    for (const t of extras) {
        const anchor = next[t.musicSrc];
        let at = anchor ? result.findIndex(e => e && e.musicSrc === anchor) : -1;
        if (at < 0 && anchor !== desiredUrl && desiredUrl) {
            // Anchor left the queue — fall back to the tapped row.
            at = result.findIndex(e => e && e.musicSrc === desiredUrl);
        }
        const key = at >= 0 ? result[at].musicSrc : '';
        const pos = at >= 0
            ? Math.min(at + 1 + (placedAfter[key] || 0), result.length)
            : result.length;
        placedAfter[key] = (placedAfter[key] || 0) + 1;
        result.splice(pos, 0, t);
    }
    const rebased = desiredUrl ? result.findIndex(e => e && e.musicSrc === desiredUrl) : desired;
    return { entries: result, desired: rebased >= 0 ? rebased : desired, anchors: next };
}

/**
 * Where a freshly (re)built queue should start.
 *
 * Keep the position of the track that is ALREADY playing when the queue
 * merely grew or shrank around it — a feed page-in appends, an
 * Add-to-Next-Up splices one entry in, a queue-panel delete removes one.
 * Re-pushing in those cases must not yank playback back to the tapped row.
 *
 * But "the playing track appears somewhere in the new queue" is NOT that
 * case by itself: a playlist Play All (or a row tap on a different surface)
 * that happens to contain the current track is a deliberate restart, and
 * keeping the old position left the audio on the previous track while every
 * surface showed the new list starting. Tell the two apart by membership:
 * growth keeps the old queue as an in-order subsequence of the new one (and
 * removal the reverse); a wholesale replacement keeps neither.
 *
 * @param {string} prevSignature  queueSignature of the queue last pushed
 * @param {Array}  entries        the new queue
 * @param {string|null} playingUrl url the plugin currently has loaded
 * @param {number} desired        start position the surface asked for
 * @returns {number}
 */
export function resolveQueueStartIndex(prevSignature, entries, playingUrl, desired) {
    const playingIdx = playingUrl
        ? (entries || []).findIndex(t => t && t.musicSrc === playingUrl)
        : -1;
    if (playingIdx < 0) return desired;
    const prevUrls = prevSignature ? prevSignature.split('|') : [];
    const urls = (entries || []).map(t => (t && t.musicSrc) || '');
    const inOrderSubsequence = (needle, haystack) => {
        if (!needle.length) return false;
        let i = 0;
        for (const u of haystack) { if (i < needle.length && u === needle[i]) i++; }
        return i === needle.length;
    };
    const keepPosition = inOrderSubsequence(prevUrls, urls) || inOrderSubsequence(urls, prevUrls);
    return keepPosition ? playingIdx : desired;
}
